import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/auth';
import { useSmsStatus } from '@/context/SmsStatusContext';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '@/components/ui/alert-dialog';
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from '@/components/ui/tooltip';
import { 
  AlertCircle, 
  Check, 
  X, 
  MessageSquare, 
  Loader, 
  HelpCircle,
  Info,
  AlertTriangle,
  WifiOff
} from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { toast } from '@/components/ui/use-toast';
import { 
  hasSuperAdminAccess, 
  hasOrgAdminAccess, 
  hasRestAdminAccess,
  UserRole
} from '@/context/auth/types';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

interface SmsStatusIndicatorProps {
  restaurantId?: string;
  organisationId?: string;
  compact?: boolean;
  showToggle?: boolean;
  showCard?: boolean;
  position?: string;
  deferRender?: boolean;
}

const renderedInstances = new Set<string>();
let primaryToggleId: string | null = null;

const DONT_SHOW_DIALOG_KEY = 'sms_indicator_dont_show_dialogs';

export const SmsStatusIndicator: React.FC<SmsStatusIndicatorProps> = ({
  restaurantId,
  organisationId,
  compact = false,
  showToggle = true,
  showCard = false,
  position = 'unknown',
  deferRender = true
}) => {
  const { user } = useAuth();
  const [isReady, setIsReady] = useState(!deferRender);
  const isMobile = useIsMobile();
  const instanceRef = React.useRef<string>(`${position}-${Math.random().toString(36).substring(2, 9)}`);
  
  const [showEnableDialog, setShowEnableDialog] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [dontShowDialogAgain, setDontShowDialogAgain] = useState(false);
  
  const [isPrimaryToggle, setIsPrimaryToggle] = useState(false);
  
  const [detailedError, setDetailedError] = useState<{
    message: string;
    type: 'network' | 'permission' | 'server' | 'unknown';
    timestamp: number;
  } | null>(null);

  let smsStatusContext;
  try {
    smsStatusContext = useSmsStatus();
    
    useEffect(() => {
      if (deferRender && !smsStatusContext.loading) {
        setIsReady(true);
      }
    }, [deferRender, smsStatusContext.loading]);
  } catch (error) {
    console.warn('SmsStatusIndicator: SMS Status context not available. Showing loading state.');
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading SMS status...</span>
      </div>
    );
  }
  
  const { 
    loading, 
    error, 
    restaurantSmsStatus, 
    organisationSmsStatus,
    checkRestaurantSmsStatus,
    checkOrganisationSmsStatus,
    enableRestaurantSms,
    disableRestaurantSms,
    enableOrganisationSms,
    disableOrganisationSms,
    canEnableSms,
    canDisableSms,
    canViewSmsStatus,
    clearCache
  } = smsStatusContext;

  const userCanEnable = restaurantId ? 
    canEnableSms(user, restaurantId) : 
    (user?.role && (hasSuperAdminAccess(user.role) || hasOrgAdminAccess(user.role)));
    
  const userCanDisable = restaurantId ? 
    canDisableSms(user, restaurantId) : 
    (user?.role && hasSuperAdminAccess(user.role));
    
  const userCanView = restaurantId ? 
    canViewSmsStatus(user, restaurantId) : 
    !!user;

  const [smsEnabled, setSmsEnabled] = useState<boolean | null>(null);
  const [enabledBy, setEnabledBy] = useState<string | null>(null);
  const [enabledDate, setEnabledDate] = useState<string | null>(null);

  useEffect(() => {
    try {
      const savedPreference = localStorage.getItem(DONT_SHOW_DIALOG_KEY);
      if (savedPreference) {
        setDontShowDialogAgain(savedPreference === 'true');
      }
    } catch (err) {
      console.warn('Error accessing localStorage:', err);
    }
  }, []);

  const saveDialogPreference = (dontShow: boolean) => {
    try {
      localStorage.setItem(DONT_SHOW_DIALOG_KEY, dontShow.toString());
      setDontShowDialogAgain(dontShow);
    } catch (err) {
      console.warn('Error saving to localStorage:', err);
    }
  };

  const processError = (err: any) => {
    let errorMessage = 'Unknown error occurred';
    let errorType: 'network' | 'permission' | 'server' | 'unknown' = 'unknown';

    if (err.message?.includes('permission') || err.message?.includes('denied')) {
      errorMessage = 'Permission denied. You do not have access to this operation.';
      errorType = 'permission';
    } else if (err.message?.includes('rate limit')) {
      errorMessage = 'Rate limit exceeded. Please try again in a few minutes.';
      errorType = 'server';
    } else if (err.message?.includes('network') || !navigator.onLine) {
      errorMessage = 'Network connection issue. Please check your internet connection.';
      errorType = 'network';
    } else if (err.message) {
      errorMessage = err.message;
      
      if (err.message.includes('403')) {
        errorType = 'permission';
      } else if (err.message.includes('timeout') || err.message.includes('ECONNREFUSED')) {
        errorType = 'network';
      } else if (err.message.includes('500')) {
        errorType = 'server';
      }
    }

    return {
      message: errorMessage,
      type: errorType,
      timestamp: Date.now()
    };
  };


  const fetchStatus = React.useCallback(async () => {
    if (!userCanView) return;

    try {
      setDetailedError(null);
      
      if (restaurantId) {
        const status = await checkRestaurantSmsStatus(restaurantId);
        setSmsEnabled(status);
      } else if (organisationId) {
        const status = await checkOrganisationSmsStatus(organisationId);
        setSmsEnabled(status);
      }
    } catch (err) {
      console.error('Error fetching SMS status:', err);
      setDetailedError(processError(err));
    }
  }, [restaurantId, organisationId, checkRestaurantSmsStatus, checkOrganisationSmsStatus, userCanView]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    const instanceId = instanceRef.current;
    
    if (!primaryToggleId && showToggle) {
      primaryToggleId = instanceId;
      setIsPrimaryToggle(true);
      console.log(`SmsStatusIndicator: Primary toggle set at ${position}`);
    }
    
    renderedInstances.add(position);
    
    return () => {
      renderedInstances.delete(position);
      
      if (primaryToggleId === instanceId) {
        primaryToggleId = null;
        console.log(`SmsStatusIndicator: Primary toggle unmounted from ${position}`);
      }
    };
  }, [position, showToggle]);

  if (deferRender && !isReady) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader className="h-4 w-4 animate-spin" />
        <span className="text-sm">Checking SMS status...</span>
      </div>
    );
  }

  const handleToggleClick = async (checked: boolean) => {
    if (dontShowDialogAgain) {
      checked ? confirmEnable() : confirmDisable();
    } else {
      if (checked) {
        setShowEnableDialog(true);
      } else {
        setShowDisableDialog(true);
      }
    }
  };

  const confirmEnable = async () => {
    try {
      let success = false;

      if (restaurantId) {
        success = await enableRestaurantSms(restaurantId);
      } else if (organisationId) {
        success = await enableOrganisationSms(organisationId);
      }

      if (success) {
        setSmsEnabled(true);
        clearCache();
        
        toast({
          title: "SMS Enabled",
          description: `SMS has been successfully enabled for this ${restaurantId ? 'restaurant' : 'organisation'}.`,
        });
        
        console.log('SMS enabled by user:', user?.id, 'for', restaurantId || organisationId);
      }
    } catch (err) {
      console.error('Error enabling SMS:', err);
      const processedError = processError(err);
      setDetailedError(processedError);
      
      toast({
        title: "Error",
        description: processedError.message,
        variant: "destructive"
      });
    } finally {
      setShowEnableDialog(false);
    }
  };

  const confirmDisable = async () => {
    try {
      let success = false;

      if (restaurantId) {
        success = await disableRestaurantSms(restaurantId);
      } else if (organisationId) {
        success = await disableOrganisationSms(organisationId);
      }

      if (success) {
        setSmsEnabled(false);
        clearCache();
        
        toast({
          title: "SMS Disabled",
          description: `SMS has been successfully disabled for this ${restaurantId ? 'restaurant' : 'organisation'}.`,
        });
        
        console.log('SMS disabled by user:', user?.id, 'for', restaurantId || organisationId);
      }
    } catch (err) {
      console.error('Error disabling SMS:', err);
      const processedError = processError(err);
      setDetailedError(processedError);
      
      toast({
        title: "Error",
        description: processedError.message,
        variant: "destructive"
      });
    } finally {
      setShowDisableDialog(false);
    }
  };

  const getErrorMessage = () => {
    if (detailedError) {
      return detailedError.message;
    }
    
    if (!error) return null;
    
    if (error.includes('rate limit')) {
      return "Rate limit exceeded. Please try again in a few minutes.";
    }
    
    if (error.includes('stripe') || error.includes('payment')) {
      return "Billing system unavailable. Please try again later.";
    }
    
    if (!organisationId && !restaurantId) {
      return "No organisation or restaurant specified.";
    }
    
    return error;
  };

  const getErrorIcon = () => {
    if (!detailedError) return <AlertCircle className="h-4 w-4" />;
    
    switch (detailedError.type) {
      case 'network':
        return <WifiOff className="h-4 w-4" />;
      case 'permission':
        return <AlertTriangle className="h-4 w-4" />;
      case 'server':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center gap-2">
          <Loader className="h-4 w-4 animate-spin text-primary" />
          <span className="text-sm font-medium">Checking SMS status...</span>
          {!compact && <Skeleton className="h-6 w-24" />}
        </div>
      );
    }

    if (detailedError || error) {
      const errorMessage = getErrorMessage();
      return (
        <div className="flex items-center gap-2 text-destructive">
          {getErrorIcon()}
          <span className="text-sm font-medium">{errorMessage || "Unable to load SMS status"}</span>
          
          {(!organisationId && !restaurantId) && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 w-6 p-0 rounded-full"
                >
                  <HelpCircle className="h-4 w-4" />
                  <span className="sr-only">More info</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>You need to specify a restaurant or organisation to check SMS status.</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      );
    }

    if (!userCanView) {
      return (
        <div className="flex items-center gap-2 text-muted-foreground">
          <AlertTriangle className="h-4 w-4" />
          <span className="text-sm">You don't have permission to view SMS status</span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-4 justify-between w-full">
        <TooltipProvider>
          <div className="flex items-center gap-2">
            {smsEnabled ? (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-2">
                      <div className="flex h-4 w-4 items-center justify-center rounded-full bg-ubereats-green">
                        <Check className="h-3 w-3 text-white stroke-white" />
                      </div>
                      <span className={compact ? "text-sm" : "text-sm font-medium"}>
                        SMS Enabled
                      </span>
                      {enabledBy && !compact && (
                        <Badge variant="secondary" className="text-xs ml-1">
                          Enabled by {enabledBy}
                        </Badge>
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="space-y-1">
                      <p className="font-medium">SMS messaging is active</p>
                      <p className="text-xs text-muted-foreground">
                        For {restaurantId ? 'this restaurant' : 'your organisation'}
                      </p>
                      {enabledDate && (
                        <p className="text-xs text-muted-foreground">
                          Enabled on: {new Date(enabledDate).toLocaleDateString('en-NZ', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </>
            ) : (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-2">
                      <div className="flex h-5 w-5 items-center justify-center rounded-full bg-red-100">
                        <X className="h-3 w-3 text-red-500" />
                      </div>
                      <span className={compact ? "text-sm" : "text-sm font-medium"}>
                        SMS Disabled
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="space-y-1">
                      <p>SMS features are currently turned off</p>
                      {userCanEnable ? (
                        <p className="text-xs text-muted-foreground">You have permission to enable SMS</p>
                      ) : (
                        <p className="text-xs text-muted-foreground">Contact an administrator to enable SMS</p>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </>
            )}
          </div>
        </TooltipProvider>

        {showToggle && (isPrimaryToggle || !primaryToggleId) && (userCanEnable || userCanDisable) && (
          <div className="flex items-center gap-2">
            {compact ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <Switch 
                        checked={!!smsEnabled} 
                        onCheckedChange={handleToggleClick} 
                        disabled={loading || 
                          (!userCanEnable && !userCanDisable) || 
                          (smsEnabled && !userCanDisable) || 
                          (!smsEnabled && !userCanEnable)}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {smsEnabled ? (
                      userCanDisable ? 
                        <p>Click to disable SMS messaging</p> : 
                        <p>You don't have permission to disable SMS</p>
                    ) : (
                      userCanEnable ? 
                        <p>Click to enable SMS messaging</p> : 
                        <p>You don't have permission to enable SMS</p>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <>
                <span className="text-sm text-muted-foreground">
                  {smsEnabled ? "Disable" : "Enable"} SMS
                </span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <Switch 
                          checked={!!smsEnabled} 
                          onCheckedChange={handleToggleClick} 
                          disabled={loading || 
                            (!userCanEnable && !userCanDisable) || 
                            (smsEnabled && !userCanDisable) || 
                            (!smsEnabled && !userCanEnable)}
                          className={
                            (!userCanEnable && !userCanDisable) || 
                            (smsEnabled && !userCanDisable) || 
                            (!smsEnabled && !userCanEnable) ? "cursor-not-allowed opacity-50" : ""
                          }
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      {smsEnabled ? (
                        userCanDisable ? 
                          <p>Click to disable SMS messaging</p> : 
                          <p>Only Super Admins can disable SMS</p>
                      ) : (
                        userCanEnable ? 
                          <p>Click to enable SMS messaging</p> : 
                          <p>You don't have permission to enable SMS</p>
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            )}
          </div>
        )}

        {!smsEnabled && !showToggle && userCanEnable && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  size="sm" 
                  onClick={() => setShowEnableDialog(true)} 
                  disabled={loading}
                  className="ml-auto"
                >
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Enable SMS
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Activate SMS capabilities for {restaurantId ? 'this restaurant' : 'your organisation'}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    );
  };

  const enableDialog = (
    <AlertDialog open={showEnableDialog} onOpenChange={setShowEnableDialog}>
      <AlertDialogContent className={isMobile ? "max-w-[90%]" : ""}>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            Enable SMS Features?
          </AlertDialogTitle>
          <AlertDialogDescription>
            <div className="space-y-4 py-2">
              <p>
                This will activate SMS capabilities for {restaurantId ? 'this restaurant' : 'your organisation'}.
                Standard SMS credit rates will apply to messages sent.
              </p>
              
              <div className="bg-muted p-3 rounded-md">
                <h4 className="font-medium text-sm flex items-center gap-1.5 mb-2">
                  <Info className="h-4 w-4 text-blue-500" />
                  SMS Pricing Information
                </h4>
                <ul className="text-xs space-y-1 text-muted-foreground">
                  <li>• Standard rate: $0.15 per SMS unit</li>
                  <li>• Messages over 160 characters use multiple units</li>
                  <li>• International messages may have higher rates</li>
                  <li>• You will only be billed for messages actually sent</li>
                </ul>
              </div>
              
              {!compact && (
                <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-md border border-amber-200 dark:border-amber-800">
                  <h4 className="font-medium text-sm flex items-center gap-1.5 mb-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-500" />
                    Important Information
                  </h4>
                  <ul className="text-xs space-y-1 text-amber-800 dark:text-amber-300">
                    <li>• SMS campaigns will be charged to your account</li>
                    <li>• Make sure your contact data is up-to-date</li>
                    <li>• Test your messages before sending to your audience</li>
                  </ul>
                </div>
              )}
              
              <div className="flex items-center space-x-2 pt-2">
                <Checkbox 
                  id="remember-dialog" 
                  checked={dontShowDialogAgain} 
                  onCheckedChange={(checked) => saveDialogPreference(!!checked)} 
                />
                <Label htmlFor="remember-dialog" className="text-sm text-muted-foreground">
                  Don't show this confirmation again
                </Label>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={confirmEnable}>Enable SMS</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  const disableDialog = (
    <AlertDialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
      <AlertDialogContent className={isMobile ? "max-w-[90%]" : ""}>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Disable SMS Features?
          </AlertDialogTitle>
          <AlertDialogDescription>
            <div className="space-y-4 py-2">
              <p>
                This will turn off SMS capabilities for {restaurantId ? 'this restaurant' : 'your organisation'}.
                You won't be able to send SMS messages until you enable it again.
              </p>
              
              <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-md border border-red-200 dark:border-red-800">
                <h4 className="font-medium text-sm flex items-center gap-1.5 mb-2">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  Warning
                </h4>
                <ul className="text-xs space-y-1 text-red-800 dark:text-red-300">
                  <li>• Any scheduled campaigns will be cancelled</li>
                  <li>• Automated SMS notifications will stop working</li>
                  <li>• Your SMS credits will remain in your account</li>
                </ul>
              </div>
              
              <div className="flex items-center space-x-2 pt-2">
                <Checkbox 
                  id="remember-dialog" 
                  checked={dontShowDialogAgain} 
                  onCheckedChange={(checked) => saveDialogPreference(!!checked)} 
                />
                <Label htmlFor="remember-dialog" className="text-sm text-muted-foreground">
                  Don't show this confirmation again
                </Label>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction 
            onClick={confirmDisable}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Disable SMS
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  const content = (
    <>
      {renderContent()}
      {enableDialog}
      {disableDialog}
    </>
  );

  if (showCard) {
    return (
      <Card className="w-full">
        <CardContent className="pt-6">
          {content}
        </CardContent>
      </Card>
    );
  }

  return content;
};
